const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form

    /**
     * @type {CollectionFiltersPaginationRender}
     * @public
     */
    this.paginationRenderer = new CollectionFiltersPaginationRender(this)
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html, e)
    this.renderProductCount(html)
    this.paginationRenderer.render(html)

    this.form.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')

    let countsToRender

    if (detailSelector) {
      const facetDetailElements = getElements(detailSelector, {
        context: parsedHTML,
      })
      /**
       * @param {HTMLElement} element
       *
       * @returns {Boolean}
       */
      const matchesIndex = element => {
        const facet = e ? e.target.closest(detailSelector) : null

        return facet ? element.dataset.index === facet.dataset.index : false
      }
      const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))
      countsToRender = facetDetailElements.find(el => matchesIndex(el))

      this.form.log('renderFilters', {
        e,
        parsedHTML,
        facetDetailElements,
        facetsToRender,
        countsToRender,
      })

      facetsToRender.forEach(element => {
        const selector = detailSelector
          .split(',')
          .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
          .join(',')

          if (selector) {
            const el = getElement(selector);
            if (el) el.innerHTML = element.innerHTML;
          }
      })
    }

    this.renderActiveFacets(parsedHTML)
    this.renderAdditionalElements(parsedHTML)

    if (countsToRender) {
      this.renderSelectedFacetCount(
        countsToRender,
        e.target.closest(detailSelector),
      )
    }
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    this.activeFacetSelectors.forEach(selector => {
      const activeFacetElement = parsedHTML.querySelector(selector)

      this.form.log('renderActiveFacets', {
        parsedHTML,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      getElements(selector).forEach(
        el => (el.innerHTML = activeFacetElement.innerHTML),
      )
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {}

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const direction = this.getPaginationDirection(e)
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const newItems = document.createDocumentFragment()

    const newElements = getElements(
      '[data-collection-filters-form-external-el="item"]',
      {
        context: parsedHTML,
      },
    )

    newElements.forEach(el => newItems.appendChild(el))

    if (direction == null) {
      this.form.externalEls['item-container'].element.innerHTML = ''
      this.form.externalEls['item-container'].element.append(newItems)
    } else if (direction === 'PREVIOUS') {
      this.form.externalEls['item-container'].element.prepend(newItems)
    } else if (direction === 'NEXT') {
      this.form.externalEls['item-container'].element.append(newItems)
    }

    document
      .querySelectorAll(
        '[data-collection-filters-form-external-el="item"] img[srcset]',
      )
      .forEach(image => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })

    document
      .querySelector(
        '[data-collection-filters-form-external-el="productpanel"]',
      )
      .setAttribute('aria-hidden', newElements.length === 0)
    document
      .querySelector('[data-collection-filters-form-external-el="emptypanel"]')
      .setAttribute('aria-hidden', newElements.length > 0)
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(this.form?.externalEls['product-count']?.selector)?.innerHTML
    this.form.log('renderProductCount', {
      html,
      count,
      countElements: this.countElements,
    })
    if (!count) return

    this.countElements.forEach(element => (element.innerHTML = count))
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.form.externalEls['load-previous'].selector}, ${this.form.externalEls['load-more'].selector}`,
    )
    if (!paginationElement) return null

    return paginationElement === this.form.externalEls['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets']?.selector,
        this.form.externalEls['active-facets']?.selector,
      ]
    }

    return [this.form.els['active-facets']?.selector]
  }
}

class CollectionFiltersPaginationRender {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = renderer
  }

  /**
   * @param {string} html
   */
  render (html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    this.renderLoadPrevious(parsedHTML)
    this.renderLoadMore(parsedHTML)
    this.renderCount(parsedHTML)
    this.renderActiveFilterCount(parsedHTML)
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (
      !this.shouldShowLoadPrevious &&
      this.form.externalEls['load-previous']
    ) {
      return (this.form.externalEls['load-previous'].element.innerHTML = '')
    }

    if (!this.shouldRerenderLoadPrevious) return

    return this._renderEl('load-previous', html)
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldRerenderLoadMore) return

    this._renderEl('load-more', html)

    if (!this.shouldShowLoadMore) {
      const loadMoreTrigger = getElement(
        '[data-collection-filters-form-external-el="load-more-trigger"]',
      )

      if (!loadMoreTrigger) return
      loadMoreTrigger.innerHTML = ''
    }
  }

  /**
   * @param {Document} html
   */
  renderCount (html) {
    return this._renderEl('count', html)
  }

  /**
   * @param {Document} html
   */
  renderActiveFilterCount (html) {
    const external = this.form.externalEls['active-filter-count']
    if (!external?.exists) return
  
    const source = getElement(external.selector, { context: html })
    if (!source) return
  
    getElements(external.selector).forEach(el => {
      el.innerHTML = source.innerHTML
    })
  }

  /**
   * @param {string} section
   * @param {Document} html
   */
  _renderEl (section, html) {
    if (this.form.externalEls[section]) {
      this.form.externalEls[section].element.innerHTML = getElement(
        this.form.externalEls[section].selector,
        { context: html },
      ).innerHTML
    }
  }

  get form () {
    return this.renderer.form
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `${this.form.externalEls.item?.selector}[data-page="1"]`,
    )

    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldShowLoadMore () {
    const anElementFromTheLastPageExists = getElement(
      `${this.form.externalEls.item?.selector}[data-page="${this.lastPage}"]`,
    )
    if (anElementFromTheLastPageExists) return false

    return Number(this.currentPage) < this.lastPage
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.form.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    if (!this.form.externalEls.item?.selector) return 0
    const productGridItems = getElements(
      `${this.form.externalEls.item.selector}`,
    )

    return Number(productGridItems[0].dataset.page) || 1
  }

  get currentHighestPage () {
    if (!this.form.externalEls.item?.selector) return 0
    const productGridItems = getElements(
      `${this.form.externalEls.item.selector}`,
    )

    return (
      Number(productGridItems[productGridItems.length - 1].dataset.page) || 1
    )
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(
      this.form.externalEls['product-grid'].element.dataset.lastPage,
    )
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  // static get requiredElements () {
  //   return ['facets', 'facet', 'facet-summary']
  // }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  static isPaginating = false

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.name,
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 500)
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    if (this.els?.facets?.exists) {
      this.listeners.add(
        this.els.facets.element,
        'keyup',
        this.onKeyUp.bind(this)
      );
    }

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onLinkClick.bind(this))
    this.listeners.add(document, 'click', this.onToTopClick.bind(this))

    if (window.location.hash) {
      this.repositionToElement()
    }
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.previous
    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    this.form.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    if (!this.els?.facet?.exists) return;
    e.preventDefault()

    const activeFacet = e.target.closest('[data-collection-filters-form-el="active-facet"]');
    const clearFilters = e.target.closest('[data-collection-filters-form-el="clear-filters"]');

    if (activeFacet !== null) {
      const category = activeFacet.dataset.filterCategory;
      let value = null
      let valueMin = null
      let valueMax = null

      if (activeFacet.dataset.priceFilter === 'true') {
        valueMin = activeFacet.dataset.valueMin
        valueMax = activeFacet.dataset.valueMax
      } else {
        value = activeFacet.dataset.filterValue;
      }

      const eventData = {
        event: 'active_filter_removal',
        filter_category: category,
        filter_value: value,
        filter_value_min: valueMin,
        filter_value_max: valueMax,
        page_type: 'collection'
      }

      this.log('active_filter_removal', eventData)

      window.dataLayer.push(eventData);
    }

    if (clearFilters !== null) {
      const eventData = {
        event: 'clear_filters',
        page_type: 'collection'
      }

      this.log('clear_filters', eventData)

      window.dataLayer.push(eventData);
    }

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (!this.els?.facet?.exists) return;
    if (e.code.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return
    if (this.constructor.isPaginating) return

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    this.constructor.isPaginating = true

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({ searchParams: currentSearchParams.toString(), e })
  }

  /**
   * @param {PointerEvent} e
   */
  onToTopClick (e) {
    if (!e.target.closest('[data-collection-filters-form-external-el="top"]')) {
      return
    }

    this.scrollToTop()
  }

  /**
   * @param {PointerEvent} e
   */
  onLinkClick (e) {
    if (!e.target.closest('a')) return

    const linkHasPageReference = e.target.closest('[data-page]')
    if (!linkHasPageReference) return true

    const imageHasLinkReference = e.target.closest('.prd-Card_RelatedLink')
    if (!imageHasLinkReference) return

    e.preventDefault()

    this.searchParams.current.set('page', linkHasPageReference.dataset.page)

    let newUrl = `${window.location.pathname}${this.searchParams.current &&
      '?'.concat(this.searchParams.current.toString())}`

    if (linkHasPageReference.hasAttribute('id')) {
      newUrl = `${newUrl}#${linkHasPageReference.getAttribute('id')}`
    }

    history.pushState(
      { searchParams: this.searchParams.current.toString() },
      '',
      newUrl,
    )

    window.location.href = imageHasLinkReference.getAttribute('href')
  }

  repositionToElement () {
    const callback = () => {
      const el = getElement(window.location.hash)
      if (!el) return

      const { top } = el.getBoundingClientRect()
      const headerHeight = getElement('#header')?.offsetHeight || 0
      const offset = 20

      window.setTimeout(() => {
        window.scrollTo(0, window.scrollY + (top - headerHeight - offset))
      }, 100)
    }

    if (document.readyState === 'complete') {
      callback()
    } else {
      document.addEventListener('readystatechange', callback)
    }
  }

  scrollToTop () {
    const callback = () => {
      window.setTimeout(() => {
        window.scrollTo(0, 0)
      }, 100)
    }

    if (document.readyState === 'complete') {
      callback()
    } else {
      document.addEventListener('readystatechange', callback)
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
    this.trackSelectedSearchParam(e.target)
  }

  /**
   * @param {Target} element
   *
   * @returns {void}
   */
  trackSelectedSearchParam(target) {
    if (!target) return

    let eventData = null
    let value = target.value

    if (target.name === 'sort_by') {
      eventData = {
        event: 'sort_engagement',
        sort_value: value,
        page_type: 'collection'
      }

      this.log('sort_engagement', eventData)
    } else {
      if (target.dataset.priceFilter === 'true') {
        value = !isNaN(parseFloat(target.value)) ? Math.floor(parseFloat(target.value).toFixed(2) * 100) : 0
      }

      eventData = {
        event: 'filter_engagement',
        filter_category: target.getAttribute('data-filter-category'),
        filter_value: value,
        action: target.type === 'checkbox' ? (target.checked ? 'apply' : 'remove') : 'apply',
        page_type: 'collection'
      }

      this.log('filter_engagement', eventData)
    }

    window.dataLayer.push(eventData)
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )

    if (val === false) {
      this.constructor.isPaginating = false
    }
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    getElements('input', { context: this }).forEach(el =>
      this.listeners.add(el, 'change', this.onRangeChange),
    )
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.currentTarget)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    if (!this.els?.summary?.exists) return;
    if (!e.target.closest(this.els.summary.selector)) return

    e.preventDefault()

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [1, 0],
        transform: ['none', 'translateY(-15px)'],
      },
      { duration: 400, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      {
        opacity: [0, 1],
        transform: ['translateY(-15px)', 'none'],
      },
      { duration: 400, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }

  get content () {
    return getElement(this.els.content.selector, {
      context: this,
    })
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}

class FacetSort extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', e => {
      if (!this.els?.trigger?.exists) return;
      if (!e.target.closest(this.els?.trigger?.selector)) return

      const triggerEl = e.target.closest(this.els.trigger.selector)
      this.formSort.value = triggerEl.dataset.value

      this.formSort.dispatchEvent(new Event('input', { bubbles: true }))

      this.els.trigger.elements.forEach(trigger => {
        if (trigger === triggerEl) {
          trigger.setAttribute('aria-current', 'page')
        } else {
          trigger.removeAttribute('aria-current')
        }
      })

      this.externalEls.drawertrigger.element.textContent = triggerEl.textContent
    })
  }

  get formSort () {
    const selector = '[data-collection-filters-form-el="sort"]'

    return document.querySelector(selector)
  }
}

if (!customElements.get('facet-sort')) {
  customElements.define('facet-sort', FacetSort)
}

class ScrollingFilterSort extends window.BAO.CustomElement() {
  connectedCallback() {
    this.filterTrigger = this.externalEls['filter-trigger']?.element
    if (!this.filterTrigger) return

    this.updateAriaHidden()

    window.addEventListener('scroll', () => {
      this.updateAriaHidden()
    }, { passive: true })
  }

  updateAriaHidden() {
    const aboveViewport = this.filterTrigger.getBoundingClientRect().top < 0
    const newAriaHiddenValue = (!aboveViewport).toString()
    this.setAttribute('aria-hidden', newAriaHiddenValue)
  }
}

if (!customElements.get('scrolling-filter-sort')) {
  customElements.define('scrolling-filter-sort', ScrollingFilterSort);
}
